import { createSHA256, hashApplicationBlob } from './applicationIntegrity.js';
import { createDownloadCache } from './applicationTransferCache.js';

// Contrôles binaires : ne jamais enregistrer une page d'erreur comme installateur.
export function applicationDownloadName(value) {
  return String(value || '').replace(/\\/g, '/').split('/').pop() || 'application';
}

// Chaque portion est contrôlée avant d'être conservée. Une coupure relance
// uniquement la portion courante, sans réassembler de données partielles.
export function transferDelay(ms, signal) {
  return new Promise((resolve, reject) => {
    const abort = () => { clearTimeout(timer); signal?.removeEventListener('abort', abort); reject(new DOMException('Opération annulée', 'AbortError')); };
    const timer = setTimeout(() => { signal?.removeEventListener('abort', abort); resolve(); }, ms);
    if (signal?.aborted) abort(); else signal?.addEventListener('abort', abort, { once: true });
  });
}

function transferFailure(message, permanent = false) {
  const error = new Error(message); error.permanent = permanent; return error;
}

async function waitForConnection(signal, onState) {
  if (typeof window === 'undefined' || navigator.onLine !== false) return;
  onState('Connexion interrompue. Reprise automatique au retour du réseau…');
  await new Promise((resolve, reject) => {
    const clean = () => { window.removeEventListener('online', online); signal?.removeEventListener('abort', abort); };
    const online = () => { clean(); resolve(); };
    const abort = () => { clean(); reject(new DOMException('Opération annulée', 'AbortError')); };
    window.addEventListener('online', online, { once: true });
    signal?.addEventListener('abort', abort, { once: true });
    if (signal?.aborted) abort(); else if (navigator.onLine !== false) online();
  });
}

export async function downloadApplicationChunks(url, app, {
  token, signal, cacheScope = '', onProgress = () => {}, onState = () => {},
  fetcher = fetch, cacheFactory = createDownloadCache, sleep = transferDelay,
  requestTimeout = 60000, maxAttempts = 5
}) {
  const checkAbort = () => { if (signal?.aborted) throw new DOMException('Téléchargement annulé', 'AbortError'); };
  const request = async (address, consume) => {
    for (let attempt = 0; ; attempt += 1) {
      checkAbort();
      await waitForConnection(signal, onState);
      const controller = new AbortController();
      const cancel = () => controller.abort();
      signal?.addEventListener('abort', cancel, { once: true });
      let expired = false;
      const timer = setTimeout(() => { expired = true; controller.abort(); }, requestTimeout);
      try {
        const requestAddress = typeof address === 'function' ? address(attempt) : address;
        const response = await fetcher(requestAddress, { headers: { Authorization: 'Bearer ' + token }, cache: 'no-store', signal: controller.signal });
        if (!response.ok) {
          const temporary = [408, 425, 429].includes(response.status) || response.status >= 500;
          let message = 'Téléchargement refusé (HTTP ' + response.status + ').';
          if ((response.headers.get('content-type') || '').includes('json')) {
            try { const data = await response.json(); if (typeof data.message === 'string') message = data.message; } catch (_) { /* Réponse tronquée. */ }
          } else await response.body?.cancel();
          const error = transferFailure(message, !temporary);
          error.retryAfter = Math.min(60000, Math.max(0, Number(response.headers.get('retry-after')) * 1000 || 0));
          throw error;
        }
        return await consume(response, controller.signal);
      } catch (error) {
        checkAbort();
        if (error.permanent || attempt >= maxAttempts - 1) {
          if (expired) throw transferFailure('Le serveur ne répond plus. Relancez le téléchargement pour reprendre les portions conservées.');
          throw error;
        }
        onState('Connexion instable. Nouvelle tentative ' + (attempt + 2) + '/' + maxAttempts + '…');
        // Le délai concerne également la lecture du corps, pas seulement les en-têtes.
        clearTimeout(timer);
        signal?.removeEventListener('abort', cancel);
        await sleep(error.retryAfter || Math.min(15000, 1000 * (2 ** attempt)), signal);
      } finally {
        clearTimeout(timer); signal?.removeEventListener('abort', cancel);
      }
    }
  };

  onState('Vérification du fichier sur le serveur…');
  const manifest = await request(url + '&mode=manifest', async (response, requestSignal) => {
    let data;
    try { data = await response.json(); } catch (error) { if (requestSignal.aborted) throw error; throw transferFailure('Réponse serveur invalide. Vérifiez la nouvelle version du fichier PHP.', true); }
    if (!data.success || !/^[a-f0-9]{64}$/.test(data.sha256 || '') || !Number.isSafeInteger(data.file_size) || data.file_size < 1 || data.file_size > 2147483648 || data.file_name !== applicationDownloadName(app.fichier)) {
      throw transferFailure(data.message || 'Le fichier a changé ou le serveur doit être mis à jour. Actualisez le catalogue.', true);
    }
    return data;
  });
  const size = manifest.file_size;
  const chunkSize = Math.floor(Math.min(4 * 1024 * 1024, Math.max(512 * 1024, Number(manifest.chunk_size) || 2 * 1024 * 1024)));
  let currentChunkSize = chunkSize;
  const cache = await cacheFactory(cacheScope + '|' + url + '|' + manifest.sha256);
  const parts = [];
  const hash = await createSHA256(); hash.init();
  try {
    for (let offset = 0; offset < size;) {
      checkAbort();
      let length = Math.min(currentChunkSize, size - offset);
      let cached = await cache.get(offset);
      let part;
      let partHash;
      if (cached?.blob?.size > 0 && cached.blob.size <= length && /^[a-f0-9]{64}$/.test(cached.sha256 || '') && await hashApplicationBlob(cached.blob, { signal }) === cached.sha256) {
        part = cached.blob; partHash = cached.sha256;
        length = part.size;
        onState('Reprise des portions déjà vérifiées…');
      } else {
        cached = null;
        onState('Téléchargement et vérification des portions…');
        const received = await request(attempt => {
          length = Math.min(size - offset, Math.max(128 * 1024, Math.floor(currentChunkSize / (2 ** attempt))));
          return url + '&mode=chunk&offset=' + offset + '&length=' + length + '&sha256=' + manifest.sha256;
        }, async (response, requestSignal) => {
          if (response.status !== 206) {
            await response.body?.cancel();
            throw transferFailure('Le serveur doit recevoir la nouvelle version de download_application.php.', true);
          }
          const range = 'bytes ' + offset + '-' + (offset + length - 1) + '/' + size;
          let fileName = '';
          try { fileName = decodeURIComponent(response.headers.get('x-application-file') || ''); } catch (_) { /* En-tête invalide. */ }
          if (response.headers.get('content-range') !== range || fileName !== manifest.file_name || response.headers.get('x-application-sha256') !== manifest.sha256) {
            await response.body?.cancel();
            throw transferFailure('La version du fichier a changé. Actualisez le catalogue.', true);
          }
          const sha256 = response.headers.get('x-chunk-sha256');
          if (!/^[a-f0-9]{64}$/.test(sha256 || '')) { await response.body?.cancel(); throw transferFailure('Empreinte de portion absente. Mettez à jour le fichier PHP.', true); }
          const blob = await response.blob();
          if (blob.size !== length || await hashApplicationBlob(blob, { signal: requestSignal }) !== sha256) throw transferFailure('Portion incomplète ou altérée. Nouvelle tentative nécessaire.');
          return { blob, sha256 };
        });
        part = received.blob; partHash = received.sha256;
        currentChunkSize = Math.min(currentChunkSize, part.size);
        await cache.put(offset, part, partHash);
      }
      checkAbort();
      hash.update(new Uint8Array(await part.arrayBuffer()));
      parts.push(part);
      offset += part.size;
      onProgress(Math.min(99, Math.floor(offset / size * 100)));
    }
    checkAbort();
    onState('Vérification SHA-256 du fichier complet…');
    if (hash.digest('hex') !== manifest.sha256) {
      await cache.clear();
      throw transferFailure('L’empreinte du fichier ne correspond pas à l’original. Aucun installateur enregistré.', true);
    }
    const blob = new Blob(parts, { type: 'application/octet-stream' });
    await validateApplicationDownload(new Response(blob), { ...app, fichier_size: size });
    checkAbort();
    await cache.clear();
    onProgress(100); onState('Intégrité SHA-256 confirmée.');
    return blob;
  } finally { cache.close(); }
}

export async function validateApplicationDownload(response, app) {
  const contentType = (response.headers.get('content-type') || '').toLowerCase();
  if (!response.ok || response.status === 206) {
    let data;
    if (contentType.includes('json')) {
      try { data = await response.json(); } catch (_) { /* Réponse JSON invalide. */ }
    }
    throw new Error(typeof data?.message === 'string' ? data.message : 'Téléchargement incomplet ou refusé (HTTP ' + response.status + ').');
  }
  const blob = await response.blob();
  if (!blob.size) throw new Error('Le fichier téléchargé est vide.');
  const bytes = new Uint8Array(await blob.slice(0, 8).arrayBuffer());
  const ext = applicationDownloadName(app.fichier).split('.').pop().toLowerCase();
  const signatures = {
    apk: [0x50, 0x4b, 0x03, 0x04], zip: [0x50, 0x4b, 0x03, 0x04],
    msix: [0x50, 0x4b, 0x03, 0x04], msixbundle: [0x50, 0x4b, 0x03, 0x04],
    appx: [0x50, 0x4b, 0x03, 0x04], appxbundle: [0x50, 0x4b, 0x03, 0x04],
    exe: [0x4d, 0x5a], msi: [0xd0, 0xcf, 0x11, 0xe0, 0xa1, 0xb1, 0x1a, 0xe1]
  };
  const signature = signatures[ext];
  if (!signature || !signature.every((byte, index) => bytes[index] === byte)) {
    const preview = await blob.slice(0, 1024).text();
    if (/^\s*<\?php/i.test(preview)) {
      throw new Error('Le serveur renvoie le code PHP au lieu de l’exécuter. Vérifiez la configuration PHP de l’hébergement.');
    }
    if (/json|text\/|html|xml/.test(contentType) || /^\s*</.test(preview)) {
      if (blob.size < 65536) {
        let data;
        try { data = JSON.parse(await blob.text()); } catch (_) { /* Page HTML ou texte brut. */ }
        if (typeof data?.message === 'string') throw new Error(data.message);
      }
      throw new Error('Le serveur a renvoyé un message ou une page au lieu de l’installateur (HTTP ' + response.status + ', ' + (contentType || 'type inconnu') + '). Aucun fichier enregistré.');
    }
    throw new Error('Le contenu reçu ne correspond pas au format de l’application. Aucun installateur enregistré.');
  }
  // Certains hébergements annoncent les APK en text/plain : les octets du
  // fichier, puis sa taille, font foi. Ne jamais convertir ou nettoyer le corps.
  const expected = Number(app.fichier_size);
  if (Number.isSafeInteger(expected) && expected > 0 && blob.size !== expected) {
    throw new Error('Fichier incomplet : ' + blob.size + ' octets reçus sur ' + expected + ' attendus. Réessayez le téléchargement.');
  }
  // Content-Length porte sur les données encodées si HTTP utilise gzip/br.
  const length = Number(response.headers.get('content-length'));
  if (!response.headers.get('content-encoding') && Number.isSafeInteger(length) && length > 0 && length !== blob.size) {
    throw new Error('Le transfert a été interrompu. Aucun installateur enregistré.');
  }
  return blob;
}
